/**
 * Changelog — manually-maintained release notes for the Inverio MCP server
 * and platform-wide developer-facing changes.
 *
 * Public route. Linked from /mcp and the marketing footer. Adds a "what's new"
 * surface that signals active development and helps developers track
 * compatible client/SDK versions.
 */

import { useEffect } from 'react';
import { View, Text, Platform, useWindowDimensions } from 'react-native';
import Head from 'expo-router/head';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Code, Calendar } from 'lucide-react-native';
import { PublicLayout } from '@/components/PublicLayout';
import { useTrackScreen } from '@/lib/analytics';

const C = {
  surface: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  text: '#FFFFFF',
  text2: 'rgba(255,255,255,0.7)',
  text3: 'rgba(255,255,255,0.5)',
  brand: '#4fd1c5',
  brandSoft: 'rgba(79,209,197,0.16)',
  green: '#34D399',
  amber: '#FF9F0A',
  red: '#FF6B6B',
  blue: '#60A5FA',
};

type EntryKind = 'feat' | 'fix' | 'docs' | 'security' | 'breaking';

interface ChangelogItem {
  kind: EntryKind;
  text: string;
}

interface ChangelogEntry {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** Optional release tag — surfaced for human reference, not used by MCP. */
  tag?: string;
  /** Short headline summarizing the entry. */
  title: string;
  items: ChangelogItem[];
}

/**
 * Changelog source. Newest first. Add a new top entry on every PR that
 * changes the MCP server's external contract (tools, schemas, auth,
 * routing, transport).
 */
const ENTRIES: ChangelogEntry[] = [
  {
    date: '2026-05-01',
    tag: 'mcp-1.6.1',
    title: 'subScores reliability — prompt tightening + retry-on-empty',
    items: [
      {
        kind: 'fix',
        text:
          'Per-dimension subScores were dropping out of ~50% of LLM scoring runs in production due to (a) output token truncation at 8K, (b) a 1-entry schema example in the prompt that the model imitated, and (c) Mistral dropping deeply-nested fields without JSON mode. Score totals were unaffected — only the sub-criteria breakdown was empty.',
      },
      {
        kind: 'fix',
        text:
          'Tightened the system prompt with an explicit "subScores REQUIRED, never omit" contract. Expanded the schema example from 1 to 4 entries per dimension and moved the schema reference to the top of every user prompt — LLMs imitate examples, so a thin example produces thin output.',
      },
      {
        kind: 'fix',
        text:
          'Bumped scoring maxTokens from 8000 to 12000 to fit a complete 15-dimension response with 3-5 populated subScores per dimension (~9-12K tokens). Enabled Mistral response_format: json_object to enforce structural fidelity on nested payloads.',
      },
      {
        kind: 'fix',
        text:
          'Added a single whole-call retry when more than 3 of 15 dimensions return empty subScores — covers the long tail of model glitches. Retry uses a focused instruction; best-of-two outcome is kept. Retry failures are logged but do not throw — partial result still useful for the score itself.',
      },
      {
        kind: 'fix',
        text:
          'Recovered string-encoded subScores arrays (subScores: "[]") in normalization rather than silently dropping them. Added structured logging (truncation_detected, subscores_retry_triggered, subscores_retry_outcome, subscores_persistently_empty, malformed_subscores_dropped, llm_output_metrics) for ongoing observability.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.7.0',
    title: 'View layer for MCP — handlers reduced to passthroughs',
    items: [
      {
        kind: 'feat',
        text:
          'Phase 2 of MCP Data Contract remediation. Six new Postgres views (v_mcp_portfolio, v_mcp_dealflow_recent, v_mcp_startup_profile, v_mcp_startup_score_summary, v_mcp_dd_checklist, v_mcp_verification_report) absorb the joins and rollups that previously lived in TypeScript handlers.',
      },
      {
        kind: 'feat',
        text:
          'Five resource handlers and three tool handlers reduced to 5-line passthroughs against the views. Methodology vocabulary stays canonical end-to-end.',
      },
      {
        kind: 'feat',
        text:
          'inverio://startup/{id}/profile now includes founders (team) and fundraising rounds in a single round-trip — no extra queries required.',
      },
      {
        kind: 'feat',
        text:
          'inverio://startup/{id}/score-summary surfaces all four canonical Inverio methodology fields (inverio_irs, venture_label, evidence_grade, deal_readiness) directly from v_mcp_startup_score_summary, built on Phase 1 score_results_v.',
      },
      {
        kind: 'feat',
        text:
          'Verification report rollup (confirmed/contradicted/unverifiable/ddFlagged) now computed in SQL via FILTER aggregates, not by .filter().length chains in JS.',
      },
      {
        kind: 'security',
        text:
          'Methodology IP stays private — no view selects from scoring_config. Bands and labels come from the public derivations in score_results_v.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.6.0',
    title: 'Methodology vocabulary now first-class via score_results_v view',
    items: [
      {
        kind: 'feat',
        text:
          'Phase 1 of MCP Data Contract remediation. Inverio scoring vocabulary (inverio_irs, venture_label, evidence_grade, deal_readiness) is now first-class on every MCP surface that reads scores.',
      },
      {
        kind: 'feat',
        text:
          'New Postgres view score_results_v derives canonical methodology fields on read — no column renames, no data migration. Methodology weights and prompts remain proprietary.',
      },
      {
        kind: 'feat',
        text:
          'inverio://startup/{id}/score-summary now returns inverioIrs, ventureLabel, evidenceGrade, dealReadiness alongside legacy fields. Existing fields kept for backward compatibility with mcp-1.5.x clients.',
      },
      {
        kind: 'feat',
        text:
          'TypeScript: AIScoreResult.scores promotes inverioIrs, inverioIrsLabel, evidenceGrade, dealReadiness from optional to required.',
      },
      {
        kind: 'docs',
        text:
          'Sequenced as a follow-up phase to the Connector readiness phase (A1/A2/A3). Phases 2–4 (resource view layer, first-class portfolio/checklist tables, untapped resources) tracked in MCP-DATA-CONTRACT-AUDIT.md.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.5.0',
    title: 'OAuth 2.0 Dynamic Client Registration (RFC 7591)',
    items: [
      {
        kind: 'feat',
        text:
          'POST /oauth/register endpoint — clients self-register and receive a client_id (and optional client_secret) per RFC 7591. Public (PKCE-only) and confidential (client_secret_basic) clients both supported.',
      },
      {
        kind: 'feat',
        text:
          '/.well-known/oauth-authorization-server now advertises registration_endpoint and lists client_secret_basic alongside the existing none auth method.',
      },
      {
        kind: 'security',
        text:
          '/oauth/authorize and /oauth/token now validate client_id against the oauth_clients registry. Unknown or revoked clients are rejected. redirect_uri must exactly match a URI registered for the client (host case-insensitive, path/query case-sensitive).',
      },
      {
        kind: 'security',
        text:
          'Confidential clients must present client_secret via Authorization: Basic header (or client_secret form param) at the token endpoint. Secrets are stored as SHA-256 hashes only.',
      },
      {
        kind: 'docs',
        text:
          '/mcp page documents the DCR endpoint with a curl example. Existing well-known clients (Claude Desktop, Cursor, Cline, Windsurf) seeded into oauth_clients so the existing flow keeps working unchanged.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.4.0',
    title: 'OAuth 2.0 Refresh Tokens',
    items: [
      {
        kind: 'feat',
        text:
          'Access tokens are now short-lived (60 minutes) and pair with a 90-day refresh token issued on the authorization-code grant.',
      },
      {
        kind: 'feat',
        text:
          'New grant_type=refresh_token branch on /oauth/token rotates the refresh token on every use (RFC 6749 §6).',
      },
      {
        kind: 'security',
        text:
          'Refresh-token reuse is detected and revokes the entire issued chain, force-expiring the latest access key (RFC 6749 §10.4).',
      },
      {
        kind: 'docs',
        text:
          'RFC 8414 metadata advertises grant_types_supported: [authorization_code, refresh_token]. Existing 90-day api_keys continue to authenticate unchanged.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.3.0',
    title: 'MCP Resources — passive context for AI agents',
    items: [
      {
        kind: 'feat',
        text:
          '8 MCP resources exposed: methodology overview (public), portfolio, thesis, recent dealflow (user-scoped), startup profile, score summary, DD checklist, verification report (templated, access-checked).',
      },
      {
        kind: 'feat',
        text:
          'resources/list, resources/read, resources/templates/list RPC methods supported per MCP spec 2025-03-26. Discovery descriptor now includes resources alongside tools.',
      },
      {
        kind: 'feat',
        text:
          'Methodology resource intentionally high-level — dimensions, bands, evidence tiers, DD framework only. Weights, prompts, and sub-rubrics remain proprietary.',
      },
      {
        kind: 'feat',
        text:
          '/mcp page redesigned for paid investor audience — Claude (desktop + web) and ChatGPT lead the client configurations; Cursor / Cline / Windsurf demoted to a developer sub-section.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.2.0',
    title: 'OAuth 2.0 with PKCE — sign-in for MCP clients',
    items: [
      {
        kind: 'feat',
        text:
          'OAuth 2.0 Authorization Code flow with PKCE (RFC 7636 S256). MCP clients can request access tokens via /oauth/authorize without users manually copying API keys.',
      },
      {
        kind: 'feat',
        text:
          'RFC 8414 metadata discovery at /.well-known/oauth-authorization-server.',
      },
      {
        kind: 'feat',
        text:
          'Tokens are 90-day api_keys rows scoped per user. Revocable via Settings → API Keys.',
      },
    ],
  },
  {
    date: '2026-05-01',
    tag: 'mcp-1.1.0',
    title: 'Production hardening — auth-gated surface, tool annotations, /server routing',
    items: [
      {
        kind: 'breaking',
        text:
          'Discovery (GET) now requires API key authentication. Previously public; tool surface is now private to authenticated keys.',
      },
      {
        kind: 'feat',
        text:
          'Tool annotations per MCP spec 2025-06-18 (readOnlyHint, destructiveHint, idempotentHint, openWorldHint). Modern clients now auto-approve read-only tools.',
      },
      {
        kind: 'feat',
        text:
          'mcp.inverio.ai/server is the canonical MCP endpoint (path-based, replaces bare /). The bare domain redirects to /mcp — the developer info page.',
      },
      {
        kind: 'feat',
        text:
          'New /mcp public-facing info page with tool list, per-tool usage hints, copy-paste configs for Claude Desktop / Cursor / Cline / Windsurf, and Inverio JWT gating.',
      },
      {
        kind: 'fix',
        text:
          'API key auth middleware now queries the correct column (key_prefix). Prior bug rejected every authenticated request with 401.',
      },
      {
        kind: 'docs',
        text: 'Settings → API Keys now links to the full MCP guide.',
      },
    ],
  },
  {
    date: '2026-04-16',
    tag: 'mcp-1.0.0',
    title: 'MCP server initial release',
    items: [
      {
        kind: 'feat',
        text:
          '9 tools shipped: score_startup, verify_claim, check_dataroom, get_score_feedback, sync_dataroom, get_dealflow, get_portfolio, get_startup_detail, get_daily_briefing.',
      },
      {
        kind: 'feat',
        text:
          'Streamable HTTP transport (JSON-RPC 2.0 over POST). Discovery via GET /.',
      },
      {
        kind: 'feat',
        text:
          'API key authentication with prefix-indexed lookup, constant-time hash compare, per-key scopes, rate limiting, and usage metering.',
      },
      {
        kind: 'feat',
        text:
          'EU-hosted infrastructure (Mistral default LLM, EU-region Supabase). EU AI Act limited-risk classification.',
      },
    ],
  },
];

const KIND_LABEL: Record<EntryKind, string> = {
  feat: 'Feature',
  fix: 'Fix',
  docs: 'Docs',
  security: 'Security',
  breaking: 'Breaking',
};

const KIND_COLOR: Record<EntryKind, string> = {
  feat: C.green,
  fix: C.amber,
  docs: C.blue,
  security: C.red,
  breaking: C.red,
};

const KIND_BG: Record<EntryKind, string> = {
  feat: 'rgba(52,211,153,0.14)',
  fix: 'rgba(255,159,10,0.14)',
  docs: 'rgba(96,165,250,0.14)',
  security: 'rgba(255,107,107,0.14)',
  breaking: 'rgba(255,107,107,0.14)',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ChangelogScreen() {
  useTrackScreen('Changelog');
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const og: [string, string][] = [
      ['og:type', 'website'],
      ['og:url', 'https://inverio.ai/changelog'],
      ['og:title', 'Inverio Changelog — Platform & MCP Server Updates'],
      ['og:description', 'Release notes for the Inverio platform and MCP server. Track new tools, breaking changes, and security fixes.'],
      ['og:image', 'https://inverio.ai/og-image.png'],
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', 'Inverio Changelog'],
      ['twitter:description', 'Platform and MCP server release notes.'],
    ];
    const created: HTMLMetaElement[] = [];
    og.forEach(([prop, content]) => {
      const m = document.createElement('meta');
      m.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop);
      m.setAttribute('content', content);
      m.id = `changelog-og-${prop}`;
      document.head.appendChild(m);
      created.push(m);
    });
    return () => created.forEach((m) => m.remove());
  }, []);

  return (
    <PublicLayout
      title="Changelog"
      titleIcon={<Calendar size={22} color={C.text} />}
      maxWidth={920}
    >
      <Head>
        <title>Changelog — Inverio Platform & MCP Server</title>
        <meta
          name="description"
          content="Release notes for the Inverio platform and MCP server — new tools, breaking changes, security fixes."
        />
      </Head>

      <View style={{ paddingTop: isDesktop ? 24 : 8, paddingBottom: 64 }}>
        <Animated.View entering={FadeInDown.duration(400)}>
          <Text
            className="font-bold mb-3"
            style={{
              fontSize: isDesktop ? 44 : 30,
              lineHeight: isDesktop ? 50 : 36,
              color: C.text,
            }}
          >
            Changelog
          </Text>
          <Text
            style={{
              color: C.text2,
              fontSize: isDesktop ? 17 : 15,
              lineHeight: isDesktop ? 26 : 22,
            }}
            className="mb-8"
          >
            Release notes for the Inverio platform and MCP server. Watch this page for breaking
            changes, new tools, and security fixes that affect agent integrations.
          </Text>
        </Animated.View>

        {ENTRIES.map((entry, i) => (
          <Animated.View
            key={`${entry.date}-${entry.tag ?? i}`}
            entering={FadeInDown.delay(100 + i * 60).duration(350)}
            className="mb-7"
          >
            <View className="flex-row items-center gap-3 mb-3 flex-wrap">
              <Text
                style={{ color: C.text }}
                className="text-base font-bold"
                numberOfLines={1}
              >
                {entry.title}
              </Text>
            </View>
            <View className="flex-row items-center gap-3 mb-3">
              <Text
                style={{ color: C.text3 }}
                className="text-xs font-semibold"
                numberOfLines={1}
              >
                {formatDate(entry.date)}
              </Text>
              {entry.tag ? (
                <View
                  className="px-2 py-0.5 rounded-md"
                  style={{ backgroundColor: C.brandSoft }}
                >
                  <Text
                    style={{ color: C.brand, fontFamily: 'monospace' }}
                    className="text-xs font-semibold"
                    numberOfLines={1}
                  >
                    {entry.tag}
                  </Text>
                </View>
              ) : null}
            </View>

            <View
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
            >
              {entry.items.map((item, j) => (
                <View
                  key={`${entry.date}-item-${j}`}
                  className="flex-row items-start gap-3 px-4 py-3 flex-wrap"
                  style={{
                    borderBottomWidth: j === entry.items.length - 1 ? 0 : 1,
                    borderBottomColor: C.border,
                  }}
                >
                  <View
                    className="px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: KIND_BG[item.kind], minWidth: 70, alignItems: 'center' }}
                  >
                    <Text
                      style={{ color: KIND_COLOR[item.kind] }}
                      className="text-xs font-bold"
                      numberOfLines={1}
                    >
                      {KIND_LABEL[item.kind]}
                    </Text>
                  </View>
                  <Text
                    style={{ color: C.text2, flex: 1, minWidth: 200 }}
                    className="text-sm leading-5"
                  >
                    {item.text}
                  </Text>
                </View>
              ))}
            </View>
          </Animated.View>
        ))}

        <View
          className="rounded-xl px-4 py-4 mt-4 flex-row items-start gap-3"
          style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
        >
          <Code size={16} color={C.text3} />
          <Text style={{ color: C.text3 }} className="text-xs leading-5 flex-1">
            Subscribe to changelog updates by following the Inverio repo on GitHub or watching this
            page. Breaking changes are announced at least 30 days in advance via the Inverio
            newsletter and the api_keys "last_used_at" notification webhook.
          </Text>
        </View>
      </View>
    </PublicLayout>
  );
}
